import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShieldCheckIcon, ScaleIcon, DocumentTextIcon, UserGroupIcon } from '@heroicons/react/24/outline';

const GdprPage = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0a] py-12">
      <div className="container-custom max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#111111] rounded-2xl border border-[#252525] overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#F97316] to-[#e8630a] px-8 py-10 text-white">
            <h1 className="text-3xl lg:text-4xl font-display font-bold mb-2">
              RGPD - Conformité
            </h1>
            <p className="text-white/80 text-lg">
              Règlement Général sur la Protection des Données
            </p>
            <div className="mt-4 flex items-center gap-2 text-sm text-white/70">
              <ShieldCheckIcon className="h-5 w-5" />
              <span>Vos droits en matière de protection des données</span>
            </div>
          </div>

          {/* Content */}
          <div className="p-8 space-y-8">
            <div className="mb-6 pb-4 border-b border-[#252525]">
              <p className="text-[#AAAAAA] leading-relaxed">
                VADOM s'engage à respecter le Règlement Général sur la Protection des Données (RGPD) 
                ainsi que la législation locale en vigueur sur la protection des données personnelles.
              </p>
            </div>

            {/* Notre engagement */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3 flex items-center gap-2">
                <ShieldCheckIcon className="h-5 w-5 text-[#F97316]" />
                Notre engagement
              </h2>
              <ul className="space-y-2 text-[#AAAAAA] pl-6 list-disc">
                <li>Transparence totale sur la collecte et l'utilisation de vos données</li>
                <li>Respect de vos droits sur vos informations personnelles</li>
                <li>Sécurité renforcée pour protéger vos données</li>
                <li>Conservation limitée dans le temps et proportionnée</li>
              </ul>
            </section>

            {/* Vos droits */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3 flex items-center gap-2">
                <ScaleIcon className="h-5 w-5 text-[#F97316]" />
                Vos droits RGPD
              </h2>
              <div className="space-y-4">
                <div className="bg-[#0a0a0a] rounded-xl p-4">
                  <h3 className="text-white font-medium mb-2">1. Droit d'accès</h3>
                  <p className="text-[#AAAAAA] text-sm">Obtenez une copie de toutes les données que nous détenons sur vous.</p>
                </div>
                <div className="bg-[#0a0a0a] rounded-xl p-4">
                  <h3 className="text-white font-medium mb-2">2. Droit de rectification</h3>
                  <p className="text-[#AAAAAA] text-sm">Corrigez toute information inexacte ou incomplète.</p>
                </div>
                <div className="bg-[#0a0a0a] rounded-xl p-4">
                  <h3 className="text-white font-medium mb-2">3. Droit à l'effacement ("droit à l'oubli")</h3>
                  <p className="text-[#AAAAAA] text-sm">Demandez la suppression de vos données dans certains cas.</p>
                </div>
                <div className="bg-[#0a0a0a] rounded-xl p-4">
                  <h3 className="text-white font-medium mb-2">4. Droit à la limitation du traitement</h3>
                  <p className="text-[#AAAAAA] text-sm">Restreindre l'utilisation de vos données dans certaines situations.</p>
                </div>
                <div className="bg-[#0a0a0a] rounded-xl p-4">
                  <h3 className="text-white font-medium mb-2">5. Droit à la portabilité</h3>
                  <p className="text-[#AAAAAA] text-sm">Recevez vos données dans un format structuré et réutilisable.</p>
                </div>
                <div className="bg-[#0a0a0a] rounded-xl p-4">
                  <h3 className="text-white font-medium mb-2">6. Droit d'opposition</h3>
                  <p className="text-[#AAAAAA] text-sm">S'opposer au traitement de vos données pour des motifs légitimes.</p>
                </div>
              </div>
            </section>

            {/* Exercer vos droits */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3 flex items-center gap-2">
                <DocumentTextIcon className="h-5 w-5 text-[#F97316]" />
                Comment exercer vos droits ?
              </h2>
              <div className="bg-[#0a0a0a] rounded-xl p-5 space-y-3">
                <p className="text-[#AAAAAA]">Pour exercer l'un de vos droits, contactez notre Délégué à la Protection des Données (DPO) :</p>
                <p className="text-white">📧 <a href="mailto:vadomxpress@gmail.com" className="hover:text-[#F97316] transition">vadomxpress@gmail.com</a></p>
                <p className="text-white">📝 Formulaire dédié : <Link to="/contact" className="text-[#F97316] hover:underline">https://vadom.tech/contact</Link></p>
                <p className="text-[#AAAAAA] text-sm mt-3">
                  Nous nous engageons à répondre à votre demande dans un délai maximum d'un mois.
                </p>
              </div>
            </section>

            {/* Base légale */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3 flex items-center gap-2">
                <UserGroupIcon className="h-5 w-5 text-[#F97316]" />
                Base légale du traitement
              </h2>
              <ul className="space-y-1 text-[#AAAAAA] pl-6 list-disc">
                <li>Exécution du contrat (création de compte, réservations)</li>
                <li>Intérêt légitime (amélioration du service, sécurité)</li>
                <li>Obligation légale (conservation des données fiscales)</li>
                <li>Consentement (marketing, cookies non essentiels)</li>
              </ul>
            </section>

            {/* Transferts de données */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">Transferts de données</h2>
              <p className="text-[#AAAAAA] leading-relaxed">
                Vos données peuvent être transférées vers des pays tiers dans le cadre de l'utilisation 
                de services comme les hébergeurs ou les prestataires de paiement. Ces transferts sont 
                encadrés par des garanties appropriées (clauses contractuelles types, Privacy Shield, etc.).
              </p>
            </section>

            {/* Réclamations */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">Réclamations</h2>
              <p className="text-[#AAAAAA] leading-relaxed">
                Si vous estimez que vos droits ne sont pas respectés, vous avez le droit d'introduire 
                une réclamation auprès de l'autorité de contrôle compétente (Commission de Protection 
                des Données Personnelles de votre pays).
              </p>
            </section>

            {/* DPO */}
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">Délégué à la Protection des Données (DPO)</h2>
              <div className="bg-[#0a0a0a] rounded-xl p-5 space-y-2">
                <p className="text-white">👤 Monsieur [Nom du DPO]</p>
                <p className="text-white">📧 <a href="mailto:vadomxpress@gmail.com" className="hover:text-[#F97316] transition">vadomxpress@gmail.com</a></p>
                <p className="text-white">📍 VADOM SAS, Lomé, Togo</p>
              </div>
            </section>

            {/* Versions */}
            <div className="mt-6 pt-4 border-t border-[#252525] text-center text-sm text-[#666666]">
              <p>Version 1.0 - En vigueur depuis le 1er janvier 2024</p>
              <div className="flex flex-wrap justify-center gap-3 mt-3">
                <Link to="/legal/privacy" className="text-[#F97316] hover:underline">Politique de confidentialité</Link>
                <span>•</span>
                <Link to="/legal/cookies" className="text-[#F97316] hover:underline">Politique des cookies</Link>
                <span>•</span>
                <Link to="/legal/terms" className="text-[#F97316] hover:underline">Conditions d'utilisation</Link>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default GdprPage;